// src/components/fix-actions/redesign/action-log-view.tsx
//
// Redesigned Action Log. Uses the SAME real sources as the legacy tab:
//   - Events: derived from the real action entries + backend event statuses
//     + generated-asset counts (from the deploy log). No fabricated actors.
//   - Deploy log: getDeployLog, with per-row status changes routed through
//     patchDeployLogStatus + onArtifactStatusChange (the real publish chain).
// The static "All types / statuses / actors" chrome from the DC is dropped in
// favour of a single working filter dropdown + search per view.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import type { FixAction } from "../../../services/fix-actions/fix-actions-service";
import {
  fixActionsService,
  type DeployLogEntry,
  type DeployStatus,
  type ActionEventStatusItem,
} from "../../../services/fix-actions/fix-actions-service";
import { statusLabel, statusVisual, statusPillCss, Ic } from "./shared";
import { ACTION_ITEMS_PREVIEW, previewDeployLog } from "./preview";

type EventStatus = "Open" | "In-Progress" | "Closed";

export interface ActionLogViewProps {
  entries: FixAction[];
  allActions?: FixAction[];
  loading: boolean;
  error: string | null;
  onNavigateToAction?: (actionTitle: string) => void;
  onNavigateToAsset?: (assetId: string) => void;
  brandId?: string;
  completedActionIds?: Set<number>;
  initialExpandTitle?: string | null;
  onInitialExpandConsumed?: () => void;
  onArtifactStatusChange?: (artifactId: string, status: DeployStatus) => void;
}

function eventTypeLabel(sourceModule?: string): string {
  const s = String(sourceModule || "").toLowerCase();
  if (s === "antenna") return "Action from Brand Monitoring";
  if (s === "cognition") return "Action from AI Reasoning Path";
  return "Suggested Action";
}

function eventBadge(status: EventStatus) {
  if (status === "Closed") return { bg: "var(--success-bg)", text: "var(--success-text)", border: "var(--success-border)", dot: "var(--signal-green)" };
  if (status === "In-Progress") return { bg: "var(--warning-bg)", text: "var(--warning-text)", border: "var(--warning-border)", dot: "var(--infrared-coral)" };
  return { bg: "var(--muted)", text: "var(--muted-foreground)", border: "var(--border)", dot: "var(--silver-mist)" };
}

function relTime(ts: string): string {
  const d = new Date(ts).getTime();
  if (!Number.isFinite(d)) return "";
  const diff = Date.now() - d;
  const m = Math.floor(diff / 60000), h = Math.floor(diff / 3600000), days = Math.floor(diff / 86400000);
  if (m < 60) return `${Math.max(0, m)}m ago`;
  if (h < 24) return `${h}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}
function absTime(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function downloadCSV(rows: string[][], filename: string) {
  try {
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const b = new Blob([csv], { type: "text/csv" });
    const u = URL.createObjectURL(b);
    const a = document.createElement("a");
    a.href = u;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(u);
  } catch {
    /* ignore */
  }
}

const DEPLOY_STATUSES: DeployStatus[] = ["Draft", "In Review", "Ready", "Published"];

export function ActionLogView({
  entries,
  allActions,
  loading,
  error,
  onNavigateToAction,
  onNavigateToAsset,
  brandId = "default",
  completedActionIds = new Set(),
  initialExpandTitle,
  onInitialExpandConsumed,
  onArtifactStatusChange,
}: ActionLogViewProps) {
  const [sub, setSub] = useState<"events" | "deploy">("events");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<string>("all");
  const [filterMenu, setFilterMenu] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // ── real backend state ──
  const [eventStatusMap, setEventStatusMap] = useState<Map<number, ActionEventStatusItem>>(new Map());
  const [deploy, setDeploy] = useState<DeployLogEntry[]>([]);
  const [deployLoading, setDeployLoading] = useState(true);
  const [statusMenuId, setStatusMenuId] = useState<number | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const refreshEventStatuses = useCallback(async () => {
    try {
      const res = await fixActionsService.getEventStatuses({ brandId });
      const m = new Map<number, ActionEventStatusItem>();
      (res.statuses || []).forEach((s) => m.set(s.actionId, s));
      setEventStatusMap(m);
    } catch {
      /* non-fatal */
    }
  }, [brandId]);

  const refreshDeploy = useCallback(async () => {
    setDeployLoading(true);
    try {
      const res = await fixActionsService.getDeployLog({ brandId });
      const items = res.items || [];
      // dev-only: seed sample deploy rows when there's no real data
      setDeploy(ACTION_ITEMS_PREVIEW && items.length === 0 ? previewDeployLog() : items);
    } catch {
      setDeploy(ACTION_ITEMS_PREVIEW ? previewDeployLog() : []);
    } finally {
      setDeployLoading(false);
    }
  }, [brandId]);

  useEffect(() => { refreshEventStatuses(); refreshDeploy(); }, [refreshEventStatuses, refreshDeploy]);
  useEffect(() => {
    const h = () => { refreshEventStatuses(); refreshDeploy(); };
    ["artifact_published", "event_status_updated", "deploy_log_refresh", "optimization_studio_refresh"].forEach((e) => window.addEventListener(e, h));
    return () => ["artifact_published", "event_status_updated", "deploy_log_refresh", "optimization_studio_refresh"].forEach((e) => window.removeEventListener(e, h));
  }, [refreshEventStatuses, refreshDeploy]);

  const generatedCountByAction = useMemo(() => {
    const m = new Map<number, number>();
    deploy.forEach((d) => m.set(d.actionId, (m.get(d.actionId) ?? 0) + 1));
    return m;
  }, [deploy]);

  const deriveStatus = useCallback((a: FixAction): EventStatus => {
    const be = eventStatusMap.get(a.id);
    const gen = generatedCountByAction.get(a.id) ?? 0;
    if (be) {
      if (be.eventStatus === "Closed") return "Closed";
      if (be.eventStatus === "In-Progress") return "In-Progress";
      if (be.eventStatus === "Open" && gen > 0) return "In-Progress";
    }
    if (completedActionIds.has(a.id)) return "Closed";
    const s = (a.status || "").toLowerCase();
    if (s === "completed") return "Closed";
    if (s === "in-progress" || s === "in_progress") return "In-Progress";
    if (gen > 0) return "In-Progress";
    return "Open";
  }, [eventStatusMap, generatedCountByAction, completedActionIds]);

  const events = useMemo(
    () => (entries || []).map((a) => ({ action: a, status: deriveStatus(a), ts: a.createdAt || new Date().toISOString(), type: eventTypeLabel((a as any).sourceModule) })),
    [entries, deriveStatus]
  );

  // deep-link expand
  useEffect(() => {
    if (!initialExpandTitle || sub !== "events" || events.length === 0) return;
    const needle = initialExpandTitle.trim().toLowerCase();
    const match = events.find((e) => e.action.title.trim().toLowerCase() === needle);
    if (!match) return;
    const id = String(match.action.id);
    setExpanded((s) => ({ ...s, [id]: true }));
    requestAnimationFrame(() => rowRefs.current.get(id)?.scrollIntoView({ behavior: "smooth", block: "center" }));
    onInitialExpandConsumed?.();
  }, [initialExpandTitle, events, sub, onInitialExpandConsumed]);

  const actionTitleById = useMemo(() => {
    const m = new Map<number, string>();
    (allActions ?? entries).forEach((a) => { if (a.id && a.title) m.set(a.id, a.title); });
    return m;
  }, [allActions, entries]);

  const handleDeployStatus = async (id: number, status: DeployStatus) => {
    setStatusMenuId(null);
    const entry = deploy.find((e) => e.id === id);
    setDeploy((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
    try {
      await fixActionsService.patchDeployLogStatus({ id, status, brandId });
      if (entry?.artifactId && onArtifactStatusChange) onArtifactStatusChange(entry.artifactId, status);
    } catch {
      if (entry) setDeploy((prev) => prev.map((e) => (e.id === id ? { ...e, status: entry.status } : e)));
    }
  };

  // ── filtering ──
  const EVENT_FILTERS = [
    { k: "all", label: "All events" },
    { k: "Open", label: "Open" },
    { k: "In-Progress", label: "In progress" },
    { k: "Closed", label: "Closed" },
  ];
  const DEPLOY_FILTERS = [
    { k: "all", label: "All assets" },
    { k: "Draft", label: "Draft" },
    { k: "In Review", label: "In Review" },
    { k: "Ready", label: "Ready to publish" },
    { k: "Published", label: "Published" },
  ];
  const activeFilters = sub === "events" ? EVENT_FILTERS : DEPLOY_FILTERS;
  const filterLabel = activeFilters.find((o) => o.k === filter)?.label || activeFilters[0].label;

  const filteredEvents = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter((e) => {
      if (filter !== "all" && e.status !== filter) return false;
      if (q && !(e.action.title.toLowerCase().includes(q) || e.type.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [events, filter, search]);

  const filteredDeploy = useMemo(() => {
    const q = search.trim().toLowerCase();
    return deploy.filter((d) => {
      if (filter !== "all" && d.status !== filter) return false;
      if (q && !(d.artifactName.toLowerCase().includes(q) || (actionTitleById.get(d.actionId) || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [deploy, filter, search, actionTitleById]);

  const onSubChange = (s: "events" | "deploy") => { setSub(s); setFilter("all"); setSearch(""); };

  const exportNow = () => {
    const ts = new Date().toISOString().slice(0, 10);
    if (sub === "events") {
      const rows = [["Time", "Event", "Status", "Action"], ...filteredEvents.map((e) => [absTime(e.ts), e.type, e.status, e.action.title])];
      downloadCSV(rows, `action-log-events-${ts}.csv`);
    } else {
      const rows = [["Updated", "Asset", "Type", "Status", "Action"], ...filteredDeploy.map((d) => [absTime(d.updatedAt || d.createdAt), d.artifactName, d.artifactType, d.status, actionTitleById.get(d.actionId) || ""])];
      downloadCSV(rows, `deploy-log-${ts}.csv`);
    }
  };

  const subBtn = (active: boolean): React.CSSProperties => ({
    position: "relative",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "10px 2px",
    fontSize: 14,
    fontWeight: active ? 600 : 500,
    color: active ? "var(--foreground)" : "var(--muted-foreground)",
  });

  return (
    <div>
      {/* sub-tabs */}
      <div style={{ display: "flex", alignItems: "center", gap: 22, borderBottom: "1px solid var(--border)", marginBottom: 4 }}>
        <button onClick={() => onSubChange("events")} style={subBtn(sub === "events")}>
          Activity
          {sub === "events" && <span style={{ position: "absolute", left: 0, right: 0, bottom: -1, height: 2, background: "var(--alpha-teal)", borderRadius: "2px 2px 0 0" }} />}
        </button>
        <button onClick={() => onSubChange("deploy")} style={subBtn(sub === "deploy")}>
          Deploy log
          {sub === "deploy" && <span style={{ position: "absolute", left: 0, right: 0, bottom: -1, height: 2, background: "var(--alpha-teal)", borderRadius: "2px 2px 0 0" }} />}
        </button>
      </div>

      {/* toolbar */}
      <div style={{ padding: "14px 0 18px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--muted-foreground)" }}>
          {sub === "events" ? `${filteredEvents.length} events` : `${filteredDeploy.length} deployments`}
        </span>
        <div style={{ position: "relative" }}>
          <button
            onClick={(e) => { e.stopPropagation(); setFilterMenu((v) => !v); }}
            style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 500, color: "var(--foreground)", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: "7px 12px", cursor: "pointer", whiteSpace: "nowrap" }}
          >
            <Ic paths={["M3 6h18M7 12h10M11 18h2"]} size={14} sw={1.7} color="var(--muted-foreground)" />
            {filterLabel}
            <Ic paths={["m6 9 6 6 6-6"]} size={12} sw={2} />
          </button>
          {filterMenu && (
            <>
              <div onClick={() => setFilterMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
              <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50, minWidth: 190, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "var(--elevation-md)", padding: 6 }}>
                {activeFilters.map((o) => {
                  const active = filter === o.k;
                  return (
                    <button
                      key={o.k}
                      onClick={() => { setFilter(o.k); setFilterMenu(false); }}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, width: "100%", textAlign: "left", fontSize: 13, fontWeight: active ? 600 : 500, color: active ? "var(--alpha-teal)" : "var(--foreground)", background: active ? "var(--teal-pill-bg, var(--alpha-teal-10))" : "transparent", border: "none", borderRadius: 7, padding: "8px 10px", cursor: "pointer" }}
                    >
                      {o.label}
                      {active && <Ic paths={["m5 12 5 5L20 7"]} size={14} sw={2.4} />}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", display: "inline-flex" }}>
            <Ic paths={["M11 11m-8 0a8 8 0 1 0 16 0a8 8 0 1 0 -16 0", "m21 21-4.3-4.3"]} size={15} sw={1.7} color="var(--muted-foreground)" />
          </span>
          <input
            className="ai-input"
            type="text"
            placeholder={sub === "events" ? "Search events..." : "Search assets..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220, maxWidth: "46vw", padding: "8px 12px 8px 34px", border: "1px solid var(--border)", borderRadius: 8, background: "var(--background)", fontSize: 13, fontFamily: "var(--font-sans)", color: "var(--foreground)", outline: "none" }}
          />
        </div>
        <button onClick={exportNow} style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 500, color: "var(--foreground)", background: "var(--background)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 13px", cursor: "pointer" }}>
          <Ic paths={["M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"]} size={14} sw={1.7} color="var(--muted-foreground)" />
          Export
        </button>
      </div>

      {/* ── loading / error / body ── */}
      {(loading && sub === "events") || (deployLoading && sub === "deploy") ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0" }}>
          <Loader2 className="w-7 h-7 animate-spin" style={{ color: "var(--signal-blue)" }} />
        </div>
      ) : error && sub === "events" ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 16px" }}>
          <div style={{ textAlign: "center", maxWidth: 420 }}>
            <AlertTriangle className="w-9 h-9" style={{ color: "var(--infrared-coral)", margin: "0 auto 14px" }} />
            <p style={{ color: "var(--muted-foreground)" }}>{error}</p>
          </div>
        </div>
      ) : sub === "events" ? (
        filteredEvents.length === 0 ? (
          <EmptyLog title="Nothing logged yet" body="Every asset generated, reviewed, and published will be traced here as you work through your actions." />
        ) : (
          <div style={{ border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden", boxShadow: "var(--elevation-sm)" }}>
            {filteredEvents.map((e, i) => {
              const id = String(e.action.id);
              const open = !!expanded[id];
              const b = eventBadge(e.status);
              const det = e.action;
              return (
                <div key={id} ref={(el) => { if (el) rowRefs.current.set(id, el); }} style={{ borderBottom: i < filteredEvents.length - 1 ? "1px solid var(--border)" : "none" }}>
                  <div onClick={() => setExpanded((s) => ({ ...s, [id]: !s[id] }))} style={{ display: "grid", gridTemplateColumns: "20px 12px 1.8fr 0.7fr 1.6fr 0.9fr", gap: 12, alignItems: "center", padding: "13px 18px", cursor: "pointer" }}>
                    <span style={{ display: "inline-flex", color: "var(--muted-foreground)", transition: "transform .2s", transform: open ? "rotate(90deg)" : "rotate(0deg)" }}>
                      <Ic paths={["m9 18 6-6-6-6"]} size={14} sw={2} />
                    </span>
                    <span style={{ width: 8, height: 8, borderRadius: 9999, background: b.dot }} />
                    <span style={{ fontSize: 13, fontWeight: 500, color: "var(--foreground)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.type}</span>
                    <span style={{ fontSize: 12, color: "var(--muted-foreground)", whiteSpace: "nowrap" }}>{relTime(e.ts)}</span>
                    <button
                      onClick={(ev) => { ev.stopPropagation(); onNavigateToAction?.(e.action.title); }}
                      style={{ justifySelf: "start", maxWidth: "100%", fontSize: 12, fontWeight: 500, color: "var(--signal-blue)", background: "var(--signal-blue-10)", border: "1px solid var(--signal-blue-20)", borderRadius: 6, padding: "3px 9px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer" }}
                    >
                      {e.action.title}
                    </button>
                    <span style={{ justifySelf: "end", ...eventPill(b) }}>{e.status}</span>
                  </div>
                  {open && (
                    <div style={{ padding: "4px 18px 16px 50px", background: "var(--muted)", display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
                      <LogField label="Timestamp" value={absTime(e.ts)} />
                      <LogField label="Source" value={e.type} />
                      <LogField label="Status" value={e.status} valueColor={b.text} />
                      {det.strategicTheme && <LogField label="Theme" value={det.strategicTheme} />}
                      {det.priority && <LogField label="Priority" value={String(det.priority)} />}
                      {typeof det.impactScore === "number" && <LogField label="Impact" value={`${det.impactScore}/100`} />}
                      {det.observation && (
                        <div style={{ gridColumn: "1 / -1" }}>
                          <FieldLabel>What we observed</FieldLabel>
                          <p style={{ fontSize: 13, color: "var(--foreground)", lineHeight: 1.55, margin: "4px 0 0" }}>{det.observation}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : filteredDeploy.length === 0 ? (
        <EmptyLog title="No deployments yet" body="When you mark a generated asset as published, it appears here with its deploy status. Nothing has been deployed yet." />
      ) : (
        <div style={{ border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden", boxShadow: "var(--elevation-sm)" }}>
          {filteredDeploy.map((d, i) => {
            const label = statusLabel(d.status);
            const v = statusVisual(label);
            return (
              <div key={d.id} style={{ borderBottom: i < filteredDeploy.length - 1 ? "1px solid var(--border)" : "none", display: "grid", gridTemplateColumns: "12px 2fr 1.4fr 0.8fr 1fr", gap: 12, alignItems: "center", padding: "13px 18px", background: "var(--background)" }}>
              <span style={{ width: 8, height: 8, borderRadius: 9999, background: v.bar }} />
                <button
                  onClick={() => onNavigateToAsset?.(d.artifactId)}
                  style={{ justifySelf: "start", textAlign: "left", fontSize: 13.5, fontWeight: 600, color: "var(--foreground)", background: "none", border: "none", padding: 0, cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}
                >
                  {d.artifactName}
                </button>
                <button
                  onClick={() => onNavigateToAction?.(actionTitleById.get(d.actionId) || "")}
                  style={{ justifySelf: "start", maxWidth: "100%", fontSize: 12, color: "var(--muted-foreground)", background: "none", border: "none", padding: 0, cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {actionTitleById.get(d.actionId) || `Action #${d.actionId}`}
                </button>
                <span style={{ fontSize: 12, color: "var(--muted-foreground)", whiteSpace: "nowrap" }}>{relTime(d.updatedAt || d.createdAt)}</span>
                <div style={{ position: "relative", justifySelf: "end" }}>
                  <button onClick={() => setStatusMenuId(statusMenuId === d.id ? null : d.id)} style={{ ...statusPillCss(label), cursor: "pointer" }}>
                    {label}
                    <Ic paths={["m6 9 6 6 6-6"]} size={11} sw={2} />
                  </button>
                  {statusMenuId === d.id && (
                    <>
                      <div onClick={() => setStatusMenuId(null)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
                      <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 50, minWidth: 160, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "var(--elevation-md)", padding: 6 }}>
                        {DEPLOY_STATUSES.filter((s) => s !== "Published").map((s) => {
                          const active = statusLabel(s) === label;
                          return (
                            <button key={s} onClick={() => handleDeployStatus(d.id, s)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, width: "100%", textAlign: "left", fontSize: 13, fontWeight: active ? 600 : 500, color: active ? "var(--alpha-teal)" : "var(--foreground)", background: active ? "var(--teal-pill-bg, var(--alpha-teal-10))" : "transparent", border: "none", borderRadius: 7, padding: "8px 10px", cursor: "pointer" }}>
                              {statusLabel(s)}
                              {active && <Ic paths={["m5 12 5 5L20 7"]} size={14} sw={2.4} />}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function eventPill(b: { bg: string; text: string; border: string }): React.CSSProperties {
  return { fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 9999, background: b.bg, color: b.text, border: `1px solid ${b.border}`, whiteSpace: "nowrap" };
}
function FieldLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--muted-foreground)" }}>{children}</div>;
}
function LogField({ label, value, valueColor }: { label: string; value: string; valueColor?: string }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div style={{ fontSize: 13, color: valueColor || "var(--foreground)", fontWeight: valueColor ? 600 : 400, marginTop: 4 }}>{value}</div>
    </div>
  );
}
function EmptyLog({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ border: "1px dashed var(--silver-mist)", borderRadius: 16, background: "var(--muted)", padding: "52px 32px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
      <span style={{ width: 52, height: 52, borderRadius: 14, background: "var(--card)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted-foreground)", marginBottom: 18 }}>
        <Ic paths={["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z", "M14 2v6h6", "M9 13h6", "M9 17h4"]} size={24} sw={1.6} />
      </span>
      <div style={{ fontSize: 16, fontWeight: 600, color: "var(--foreground)", marginBottom: 7 }}>{title}</div>
      <p style={{ fontSize: 13.5, color: "var(--muted-foreground)", lineHeight: 1.6, margin: 0, maxWidth: 400 }}>{body}</p>
    </div>
  );
}
